import React, { useState, useEffect } from 'react';
import { Container, Button, Grid, Icon, Form, Segment, Message, Radio, Loader } from 'semantic-ui-react';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import 'dayjs/locale/fr'; // import locale
import QuotesApi from '../api/QuotesApi';
import { IGroupBookingSurvey } from '../Models/IGroupBookingSurvey';
import { IGroupBookingQuote } from '../Models/IGroupBookingQuote';
import SurveysApi from '../api/SurveysApi';

dayjs.locale('fr');

const questions: { id: "rate01" | "rate02" | "rate03" | "rate04" | "rate05" | "globalRate" | "comments01" | "comments02" | "comments03" | "comments04" | "comments05" | "globalComments", label: string, order: number }[] = [
  {
    id: "rate01",
    label: "Comment estimez-vous l'accueil et la visite ?",
    order: 2
  }, {
    id: "rate02",
    label: "Comment estimez-vous le lieu ?",
    order: 3
  },
  {
    id: "globalRate",
    label: "Pour commencer, comment estimez-vous l'expérience globale ?",
    order: 1
  }
];

const SondageGroupes: React.FC = () => {
  const searchParams = new URLSearchParams(document.location.search);
  const bookingId = searchParams.get('b');
  const hash = searchParams.get('h');
  const quoteId = searchParams.get('q');
  const [groupInfo, setGroupInfo] = useState<IGroupBookingQuote>();
  const [loading, setLoading] = useState<boolean>(false);
  const [proceeding, setProceeding] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const { register, setValue, control, handleSubmit, watch } = useForm<IGroupBookingSurvey>({
    mode: "onBlur",
    defaultValues: {
      hash: hash || '',
      bookingId: bookingId || '',
      globalComments: '',
    }
  });

  useEffect(() => {
    if (bookingId && quoteId && hash) {
      setLoading(true);
      const sApi = new SurveysApi();

      sApi.hasAnswered(bookingId)
        .then(() => {
          const qApi = new QuotesApi();

          qApi.getGroupBooking(bookingId, quoteId)
            .then((quoteResp) => {
              const quote = quoteResp.data;
    
              if (quote.status === "REJECTED") {
                setErrorMessage(`Votre formulaire ne peut plus être renvoyé.`);
                return;
              }
    
              if (quote.options) {
                questions.push({
                  id: "rate03",
                  label: `Comment estimez-vous l'option ${quote.options} ?`,
                  order: 4
                })
              }
              setGroupInfo(quote);
              setLoading(false);
            });
        }).catch(() => {
          // Already answered
          setErrorMessage(`Vous avez déjà répondu à l'enquête.`);
          setLoading(false);
        });
    }
    else {
      setErrorMessage(`Votre lien est invalide, veuillez vérifier que celui-ci n'est pas coupé dans votre email`);
    }
  }, [watch, bookingId, quoteId, hash]);

  const onSubmit = (data: IGroupBookingSurvey) => {
    setErrorMessage(undefined);
    setProceeding(true);

    const sApi = new SurveysApi();

    sApi.createGroupSurvey(data)
      .then((res) => {
        setProceeding(false);
        setSent(true);
      }).catch((reason) => {
        if (reason.message && reason.message.indexOf('409') >= 0)
          setErrorMessage('Une erreur est survenue, il semblerait que le sondage ait été envoyé entre-temps. Une seule soumission est possible.');
        else
          setErrorMessage('Une erreur est survenue');
        setProceeding(false);
      });
  };

  return (
    <Container fluid>
      {loading && (
        <Segment textAlign='center'>
          <Loader active inline size='small' />
          Chargement du formulaire...
        </Segment>
      )}
      {!errorMessage && !loading &&
        (<Form onSubmit={handleSubmit(onSubmit)} success={sent}>
          <div>Dans une démarche d'amélioration continue et de satisfaction de nos visiteurs, nous souhaitons recueillir votre avis sur votre passage récent à la Ferme Rainbow. Ce formulaire vous demandera moins de deux minutes à être rempli et nous permet de nous assurer d'une bonne expérience parmi nous. Merci !</div>
          {groupInfo && <div style={{ marginTop: '10px' }}><i>Rappel de votre référence de devis: <b>{groupInfo.quoteId}</b> ({groupInfo.groupName}).</i></div>}
          <Grid stackable>
            {questions
              .sort((a, b) => {
                if (a.order > b.order) {
                  return 1;
                } else if (a.order < b.order) {
                  return -1;
                } else {
                  return 0;
                }
              })
              .map((q) => <Grid.Row>
                <Grid.Column>
                  <Form.Field required>
                    <label style={{ marginTop: 10, fontSize: 18 }}>
                      {q.label}
                    </label>
                    <Controller
                      control={control}
                      name={q.id}
                      render={({ field: { value } }) => (
                        <Form.Field style={{ textAlign: 'center', padding: 10 }}>
                          <Radio
                            label='Très insatisfait'
                            name={q.id}
                            value={1}
                            disabled={sent}
                            className='rateRadio'
                            checked={value === 1}
                            onChange={(ev, data) => setValue(q.id, 1)}
                          />
                          <Radio
                            label='Insatisfait'
                            name={q.id}
                            value={2}
                            disabled={sent}
                            className='rateRadio'
                            checked={value === 2}
                            onChange={(ev, data) => setValue(q.id, 2)}
                          />
                          <Radio
                            label='Satisfait'
                            name={q.id}
                            value={3}
                            disabled={sent}
                            className='rateRadio'
                            checked={value === 3}
                            onChange={(ev, data) => setValue(q.id, 3)}
                          />
                          <Radio
                            label='Très satisfait'
                            name={q.id}
                            value={4}
                            disabled={sent}
                            className='rateRadio'
                            checked={value === 4}
                            onChange={(ev, data) => setValue(q.id, 4)}
                          />
                        </Form.Field>
                      )}
                    />
                    <div style={{ textAlign: 'center', fontSize: 36 }}>
                      {watch(q.id) === 1 && (<Icon name="frown outline" color='red' />)}
                      {watch(q.id) === 2 && (<Icon name="meh outline" color='orange' />)}
                      {watch(q.id) === 3 && (<Icon name="smile outline" color='blue' />)}
                      {watch(q.id) === 4 && (<><Icon name="heart" color='green' /> <Icon name="smile outline" color='green' /> <Icon name="heart" color='green' /></>)}
                    </div>
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>)}

            <Grid.Row>
              <Grid.Column>
                <Form.Field>
                  <label>Avez-vous des commentaires à nous faire sur l'expérience globale à la ferme ?</label>
                  <textarea rows={5} maxLength={3500} style={{ minHeight: 100, width: '100%' }} disabled={sent} {...register("globalComments")} />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            {!sent && (
              <Grid.Row>
                <Grid.Column textAlign="center">
                  <Button size="medium" color='green' icon labelPosition='right' loading={proceeding} type="submit"><Icon name='check circle' /> Envoyer vos réponses</Button>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Form>
        )}
      {sent && (
        <Message
          success
          header='Formulaire envoyé'
          content="Votre avis a bien été envoyé, merci à vous !"
        />
      )
      }
      <Message negative={true} hidden={!errorMessage}>{errorMessage}</Message>
    </Container >
  );
}

export default SondageGroupes;
